import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocale } from '../../i18n';
import { retryWaitMessage } from '../../i18n/retryWait';

// ─────────────────────────────────────────────────────────────────────
// A THROTTLED ACTION WAITS FOR AS LONG AS THE SERVER SAID (#778 Q7).
//
// A screen that is left open must stay true: «Prøv igjen om 12 minutter» read
// eleven minutes later is a lie, and a button that is enabled while the server
// will refuse it invites the refusal. So a wait is a DEADLINE, the sentence is
// derived from it every second, and the action is disabled until it passes —
// then the sentence goes away and the button comes back by itself.
//
// ── PER ACTION, BECAUSE THE BUDGETS ARE ──────────────────────────
//
// On the code step, «send the code again» spends the MAIL budget and «sign in»
// the WRONG-CODE budget. Disabling sign-in because a resend was refused would
// lock somebody out of a code they already have. So each action has its own
// deadline, and the sentence shown is the most recent refusal's.
//
// ── RE-DERIVED FROM THE CLOCK, NOT COUNTED DOWN ──────────────────
//
// A background tab or a sleeping phone gets one timer callback in place of
// hundreds, so `left - 1` per tick drifts by exactly the time nobody was
// looking. Every tick reads `Date.now()` against the deadline instead.
//
// ── ANNOUNCED ONCE ───────────────────────────────────────────────
//
// The sentence lives in each screen's `role="alert"` slot, which announces it
// when it appears. A live region whose text then changes every second would
// be re-read every second, so the ticking text sits in an `aria-live="off"`
// span: the nearest live ancestor decides whether a change is announced, and
// this one says not to. The disabled button says the rest. (Per the ARIA
// spec; not verified with a screen reader here.)
//
// ── ZERO IS «WE DO NOT KNOW» ─────────────────────────────────────
//
// Nothing readable in `Retry-After`: the sentence says «om litt», nothing is
// disabled (we cannot say until when), and it stays until the next attempt or
// `clear()`.
// ─────────────────────────────────────────────────────────────────────

interface Wait {
  /** Epoch ms the action may be tried again, or null when the server did not say. */
  until: number | null;
  /** Start order, so the newest refusal is the one on screen. */
  seq: number;
}

export interface RetryWait<A extends string> {
  /** Record a refusal of `action` with the server's `Retry-After` (0 = unknown). */
  start: (action: A, retryAfterSeconds: number) => void;
  /** Forget `action`'s wait — called as it is tried again, so an «om litt» does not outlive it. */
  release: (action: A) => void;
  /** Forget every wait — a different address, a closed dialog. */
  clear: () => void;
  /** True while `action` may not be tried again yet. */
  waiting: (action: A) => boolean;
  /** The newest live wait's sentence, ready for an alert slot; null when none. */
  notice: ReactNode | null;
}

export function useRetryWait<A extends string>(): RetryWait<A> {
  const { t, locale } = useLocale();
  const [waits, setWaits] = useState<Partial<Record<A, Wait>>>({});
  const [, setTick] = useState(0);
  const seq = useRef(0);

  const t0 = Date.now();
  const live = (Object.entries(waits) as [A, Wait][]).filter(
    ([, w]) => w.until === null || w.until > t0,
  );
  const left = (w: Wait | undefined): number =>
    w && w.until !== null ? Math.max(0, Math.ceil((w.until - t0) / 1000)) : 0;

  const counting = live.some(([, w]) => w.until !== null);
  const expired = live.length !== Object.keys(waits).length;

  // ONE INTERVAL WHILE ANYTHING IS COUNTING, and none otherwise.
  useEffect(() => {
    if (!counting) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [counting]);

  // An expired wait is dropped from state, so the button comes back without
  // anybody pressing anything.
  useEffect(() => {
    if (!expired) return;
    setWaits((prev) => {
      const at = Date.now();
      const next: Partial<Record<A, Wait>> = {};
      for (const [action, w] of Object.entries(prev) as [A, Wait][]) {
        if (w.until === null || w.until > at) next[action] = w;
      }
      return next;
    });
  }, [expired]);

  const start = useCallback((action: A, retryAfterSeconds: number) => {
    const seconds =
      Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds : 0;
    seq.current += 1;
    const wait: Wait = {
      until: seconds > 0 ? Date.now() + seconds * 1000 : null,
      seq: seq.current,
    };
    setWaits((prev) => ({ ...prev, [action]: wait }));
  }, []);

  const release = useCallback((action: A) => {
    setWaits((prev) => {
      if (!(action in prev)) return prev;
      const next = { ...prev };
      delete next[action];
      return next;
    });
  }, []);

  const clear = useCallback(() => setWaits({}), []);

  const newest = live.reduce<[A, Wait] | null>(
    (best, entry) => (best === null || entry[1].seq > best[1].seq ? entry : best),
    null,
  );

  const notice: ReactNode | null =
    newest === null ? null : (
      <span aria-live="off" data-testid="retry-wait">
        {retryWaitMessage(t, locale, left(newest[1]))}
      </span>
    );

  return {
    start,
    release,
    clear,
    waiting: (action) => left(live.find(([a]) => a === action)?.[1]) > 0,
    notice,
  };
}
